// apps/office/src/pages/office/ClientForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, User, Mail, Phone, MapPin, Home, PaintBucket,
  Users, Calendar, FileText, Building, Layers, Package, Check, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import pb from '../../lib/pocketbaseClient';
import { getNextTempJobId, getNextConvertedJobId } from '../../lib/jobIdUtils';
import { useAuth } from '../../contexts/AuthContext';

// Added 'Five Storied (G+4)' here
const BUILDING_TYPES = [
  'Single Storied',
  'Two Storied (G+1)',
  'Three Storied (G+2)',
  'Four Storied (G+3)',
  'Five Storied (G+4)',
];

const PROPERTY_TYPES = [
  '1 RK', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '5 BHK',
  'Duplex', 'Office', 'Restaurant', 'Shop', 'Others',
];
const BUDGET_RANGES = [
  '3-5 Lakhs',
  '6-9 Lakhs',
  '10-15 Lakhs',
  '16-20 Lakhs',
  'More than 20 Lakhs',
];

const SERVICE_SECTIONS = [
  {
    key: 'Design Package',
    title: 'Design Package',
    subtitle: 'Choose ONE complete package',
    icon: Layers,
    accent: 'amber',
    mode: 'single',
  },
  {
    key: 'General',
    title: 'General Services',
    subtitle: 'Choose any number of individual services',
    icon: Package,
    accent: 'blue',
    mode: 'multiple',
  },
  {
    key: 'Approval Drawing & Estimate',
    title: 'Approval & Loan Documents',
    subtitle: 'Choose any number of documents',
    icon: FileText,
    accent: 'emerald',
    mode: 'multiple',
  },
];

const ACCENT = {
  amber: {
    head: 'from-amber-500 to-orange-500',
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    text: 'text-amber-800',
    check: 'bg-amber-500',
  },
  blue: {
    head: 'from-blue-500 to-indigo-500',
    border: 'border-blue-300',
    bg: 'bg-blue-50',
    text: 'text-blue-800',
    check: 'bg-blue-500',
  },
  emerald: {
    head: 'from-emerald-500 to-teal-500',
    border: 'border-emerald-300',
    bg: 'bg-emerald-50',
    text: 'text-emerald-800',
    check: 'bg-emerald-500',
  },
};

const ClientForm = () => {
  const { user: currentUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    pincode: '',
    serviceType: 'interior',
    status: 'Lead',
    assignedSales: '',
    assignedDesigner: '',
    notes: '',
    followupDate: '',
    plotArea: '',
    buildingType: '',
    designPackage: '',
    facingDirection: '',
    requirement: '',
    propertyType: '',
    carpetArea: '',
    budget: '',
  });

  const [allServices, setAllServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null); 
  const [selectedServices, setSelectedServices] = useState([]); 

  const { data: lead, isLoading: leadLoading } = useQuery({
    queryKey: ['lead', id],
    queryFn: () =>
      pb.collection('leads').getOne(id, { expand: 'assignedSales,assignedDesigner' }),
    enabled: isEditing,
  });

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        address: lead.address || '',
        pincode: lead.pincode || '',
        serviceType: lead.serviceType || 'interior',
        status: lead.status || 'Lead',
        assignedSales: lead.assignedSales || '',
        assignedDesigner: lead.assignedDesigner || '',
        notes: lead.notes || '',
        followupDate: lead.followupDate?.split('T')[0] || '',
        plotArea: lead.plotArea || '',
        buildingType: lead.buildingType || '',
        designPackage: lead.designPackage || '',
        facingDirection: lead.facingDirection || '',
        requirement: lead.requirement || '',
        propertyType: lead.propertyType || '',
        carpetArea: lead.carpetArea || '',
        budget: lead.budget || '',
      });

      if (lead.selectedServices) {
        try {
          const parsed =
            typeof lead.selectedServices === 'string'
              ? JSON.parse(lead.selectedServices)
              : lead.selectedServices;
          if (Array.isArray(parsed)) {
            const pkg = parsed.find((s) => s.category === 'Design Package');
            const rest = parsed.filter((s) => s.category !== 'Design Package');
            if (pkg) setSelectedPackage({ id: pkg.id || pkg.name, name: pkg.name, price: pkg.price || 0 });
            setSelectedServices(rest);
          }
        } catch (e) {
          console.warn('Could not parse selectedServices:', e);
        }
      }
    }
  }, [lead]);

  const { data: salesUsers = [] } = useQuery({
    queryKey: ['salesUsers'],
    queryFn: () =>
      pb.collection('users').getFullList({ filter: 'role = "sales"', sort: 'name' }).catch(() => []),
  });
  const { data: designerUsers = [] } = useQuery({
    queryKey: ['designerUsers'],
    queryFn: () =>
      pb.collection('users').getFullList({ filter: 'role = "designer"', sort: 'name' }).catch(() => []),
  });

  useEffect(() => {
    let cancelled = false;
    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        const items = await pb.collection('price_list_items').getFullList({ sort: '-created' });
        if (!cancelled) setAllServices(items || []);
      } catch (err) {
        console.error('Failed to load price_list_items:', err);
      } finally {
        if (!cancelled) setLoadingServices(false);
      }
    };
    fetchServices();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredServices = useMemo(() => {
    if (!formData.buildingType) return [];

    const target = formData.buildingType.trim().toLowerCase();

    const matched = allServices.filter((s) => {
      const bt = (s.buildingType || '').trim().toLowerCase();
      return bt === target;
    });

    const seen = new Set();
    return matched.filter((s) => {
      const key = (s.name || s.packageType || '').trim().toLowerCase();
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [allServices, formData.buildingType]);

  const servicesByCategory = useMemo(() => {
    const groups = {
      'Design Package': [],
      General: [],
      'Approval Drawing & Estimate': [],
    };
    filteredServices.forEach((s) => {
      if (groups[s.mainCategory]) groups[s.mainCategory].push(s);
    });
    return groups;
  }, [filteredServices]);

  const toggleSinglePackage = (item) => {
    if (selectedPackage?.id === item.id) {
      setSelectedPackage(null);
      setFormData((prev) => ({ ...prev, designPackage: '' }));
    } else {
      setSelectedPackage({
        id: item.id,
        name: item.name || item.packageType,
        price: item.sellingPrice || 0,
      });
      setFormData((prev) => ({ ...prev, designPackage: item.name || item.packageType || '' }));
    }
  };

  const toggleMultiService = (category, item) => {
    setSelectedServices((prev) => {
      const exists = prev.find((s) => s.id === item.id);
      if (exists) return prev.filter((s) => s.id !== item.id);
      return [
        ...prev,
        {
          id: item.id,
          category,
          name: item.name || item.packageType,
          price: item.sellingPrice || 0,
        },
      ];
    });
  };

  const cleanForSubmission = (data) => {
    const cleaned = { ...data };

    if (cleaned.assignedSales === '' || cleaned.assignedSales === 'none')
      cleaned.assignedSales = null;
    if (cleaned.assignedDesigner === '' || cleaned.assignedDesigner === 'none')
      cleaned.assignedDesigner = null;
    if (cleaned.followupDate === '') cleaned.followupDate = null;

    const servicesToSave = [];
    if (selectedPackage) {
      servicesToSave.push({
        id: selectedPackage.id,
        category: 'Design Package',
        name: selectedPackage.name,
        price: selectedPackage.price,
      });
    }
    selectedServices.forEach((s) => {
      servicesToSave.push({
        id: s.id,
        category: s.category,
        name: s.name,
        price: s.price,
      });
    });
    cleaned.selectedServices = servicesToSave.length > 0 ? JSON.stringify(servicesToSave) : null;

    if (selectedPackage?.name) {
      cleaned.designPackage = selectedPackage.name;
    }

    Object.keys(cleaned).forEach((k) => {
      if (cleaned[k] === undefined) delete cleaned[k];
    });
    return cleaned;
  };

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const tempJobId = await getNextTempJobId();
      const submitData = { ...data, jobId: tempJobId };
      return pb.collection('leads').create(cleanForSubmission(submitData));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['leads']);
      toast.success('Client created successfully!');
      navigate('/office/clients');
    },
    onError: (err) => {
      let msg = err.message || 'Create failed';
      if (err.originalError?.data?.message) msg = err.originalError.data.message;
      toast.error('? ' + msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      let updateData = { ...data };
      if (data.status === 'Converted') {
        const currentLead = await pb.collection('leads').getOne(id);
        if (!currentLead.jobId || !currentLead.jobId.startsWith('HD')) {
          const newJobId = await getNextConvertedJobId();
          updateData.jobId = newJobId;
          toast.info(`New Converted Job ID: ${newJobId}`);
        }
      }
      return pb.collection('leads').update(id, cleanForSubmission(updateData));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['leads']);
      queryClient.invalidateQueries(['lead', id]);
      toast.success('? Client updated successfully!');
      navigate('/office/clients');
    },
    onError: (err) => toast.error('Update failed: ' + err.message),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'buildingType') {
      setSelectedPackage(null);
      setSelectedServices([]);
      setFormData((prev) => ({ ...prev, designPackage: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value === 'none' ? '' : value }));

    if (name === 'serviceType' && value !== 'building') {
      setSelectedPackage(null);
      setSelectedServices([]);
    }
  };

  const handleSave = () => {
    if (!formData.name?.trim()) { toast.error('Name is required'); return; }
    if (!formData.email?.trim()) { toast.error('Email is required'); return; }
    if (!formData.phone?.trim()) { toast.error('Phone is required'); return; }
    if (!formData.address?.trim()) { toast.error('Address is required'); return; }

    if (isEditing) {
      updateMutation.mutate({ id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const preventEnterSubmit = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  if (isEditing && leadLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    );
  }

  const isBuilding = formData.serviceType === 'building';
  const isInterior = formData.serviceType === 'interior';
  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-6 sm:p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/office/clients')}
            className="text-white/80 hover:text-white hover:bg-white/20 gap-2"
          >
            <ArrowLeft className="w-5 h-5" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              {isEditing ? '?? Edit Client' : '?? Onboard New Client'}
            </h1>
            <p className="text-white/80 mt-1 text-sm">
              {isEditing ? 'Update client information' : 'Add a new lead to your CRM'}
            </p>
          </div>
        </div>
      </div>

      <div onKeyDown={preventEnterSubmit}>
        <Card className="border-0 shadow-xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
            <CardTitle className="flex items-center gap-2 text-indigo-800">
              <User className="w-5 h-5" /> Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-indigo-700 font-medium flex items-center gap-2">
                  <User className="w-4 h-4" /> Full Name *
                </Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Enter client name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-indigo-700 font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email *
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="client@email.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-indigo-700 font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Phone *
                </Label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="9876543210"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-indigo-700 font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Address *
                </Label>
                <Input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Street, City, State"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-indigo-700 font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Pincode
                </Label>
                <Input
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  className="border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="700001"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-indigo-700 font-medium flex items-center gap-2">
                  <Home className="w-4 h-4" /> Service Type
                </Label>
                <Select
                  value={formData.serviceType}
                  onValueChange={(val) => handleSelectChange('serviceType', val)}
                >
                  <SelectTrigger className="border-indigo-200">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Replaced Emoji with Lucide Icons */}
                    <SelectItem value="interior">
                      <div className="flex items-center gap-2">
                        <PaintBucket className="w-4 h-4 text-indigo-500" />
                        <span>Interior Design</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="building">
                      <div className="flex items-center gap-2">
                        <Home className="w-4 h-4 text-indigo-500" />
                        <span>Building Plan</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* --- Building Plan Details -------------------------- */}
            {isBuilding && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200 space-y-5">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                  <Building className="w-5 h-5" /> Building Plan Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-amber-700 font-medium">Plot Area (sq.ft)</Label>
                    <Input
                      name="plotArea"
                      value={formData.plotArea}
                      onChange={handleChange}
                      className="border-amber-200"
                      placeholder="e.g., 1200"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-amber-700 font-medium">Building Type</Label>
                    <Select
                      value={formData.buildingType}
                      onValueChange={(val) => handleSelectChange('buildingType', val)}
                    >
                      <SelectTrigger className="border-amber-200">
                        <SelectValue placeholder="Select building type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {BUILDING_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-amber-700 font-medium">Facing Direction</Label>
                    <Input
                      name="facingDirection"
                      value={formData.facingDirection}
                      onChange={handleChange}
                      className="border-amber-200"
                      placeholder="e.g., East"
                    />
                  </div>
                </div>

                {/* --- Dynamic Services Picker ------------------- */}
                {formData.buildingType && (
                  <div className="pt-2 border-t border-amber-200">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                      <div>
                        <h4 className="font-semibold text-amber-900 flex items-center gap-2">
                          <Check className="w-4 h-4" /> Select Services
                        </h4>
                        <p className="text-[11px] text-amber-700/80 mt-0.5">
                          Showing options for{' '}
                          <span className="font-semibold">{formData.buildingType}</span>
                        </p>
                      </div>
                      {/* Total Price Display Removed as requested */}
                    </div>

                    {loadingServices ? (
                      <div className="flex items-center justify-center py-10 text-amber-700">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Loading services...
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {SERVICE_SECTIONS.map((section) => {
                          const items = servicesByCategory[section.key] || [];
                          if (items.length === 0) return null;
                          const acc = ACCENT[section.accent];
                          const Icon = section.icon;
                          const isSingle = section.mode === 'single';

                          return (
                            <div
                              key={section.key}
                              className={`rounded-2xl border-2 ${acc.border} overflow-hidden bg-white`}
                            >
                              <div
                                className={`bg-gradient-to-r ${acc.head} px-4 py-3 flex items-start gap-3`}
                              >
                                <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
                                  <Icon className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-bold text-white text-base leading-tight">
                                      {section.title}
                                    </div>
                                    <span className="bg-white/25 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                      {isSingle ? 'Pick 1' : 'Multi'}
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-white/90 mt-0.5">
                                    {section.subtitle}
                                  </div>
                                </div>
                              </div>

                              <div className="divide-y divide-slate-100">
                                {items.map((item) => {
                                  const isSelected = isSingle
                                    ? selectedPackage?.id === item.id
                                    : selectedServices.some((s) => s.id === item.id);

                                  const handleClick = () =>
                                    isSingle
                                      ? toggleSinglePackage(item)
                                      : toggleMultiService(section.key, item);

                                  return (
                                    <button
                                      key={item.id}
                                      type="button"
                                      onClick={handleClick}
                                      className={`w-full flex items-start gap-3 p-3.5 text-left transition-colors ${
                                        isSelected ? acc.bg : 'hover:bg-slate-50'
                                      }`}
                                    >
                                      <div
                                        className={`shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center border-2 transition-all ${
                                          isSingle ? 'rounded-full' : 'rounded-md'
                                        } ${
                                          isSelected
                                            ? `${acc.check} border-transparent`
                                            : 'border-slate-300 bg-white'
                                        }`}
                                      >
                                        {isSelected && (
                                          <Check
                                            className="w-3 h-3 text-white"
                                            strokeWidth={3.5}
                                          />
                                        )}
                                      </div>

                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 flex-wrap">
                                          <div className="flex-1 min-w-0">
                                            <div
                                              className={`font-semibold text-sm ${
                                                isSelected ? acc.text : 'text-slate-800'
                                              }`}
                                            >
                                              {item.name || item.packageType}
                                            </div>
                                            {item.details && (
                                              <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">
                                                {item.details}
                                              </div>
                                            )}
                                          </div>
                                          
                                          {/* Individual Price Display Removed as requested */}
                                          
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}

                        {Object.values(servicesByCategory).every((arr) => arr.length === 0) && (
                          <div className="text-center py-10 bg-amber-50 rounded-2xl border border-dashed border-amber-200">
                            <Package className="w-8 h-8 text-amber-300 mx-auto mb-2" />
                            <p className="text-sm text-amber-700">
                              No services configured for this building type.
                            </p>
                            <p className="text-xs text-amber-600/70 mt-1">
                              Add price list items in PocketBase with this buildingType.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-amber-700 font-medium">Special Requirements</Label>
                  <Textarea
                    name="requirement"
                    value={formData.requirement}
                    onChange={handleChange}
                    rows={2}
                    className="border-amber-200"
                    placeholder="Any special needs..."
                  />
                </div>
              </div>
            )}

            {/* --- Interior Details ------------------------------- */}
            {isInterior && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200">
                <h3 className="font-semibold text-emerald-800 flex items-center gap-2 mb-4">
                  <PaintBucket className="w-5 h-5" /> Interior Design Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-emerald-700 font-medium">Property Type</Label>
                    <Select
                      value={formData.propertyType}
                      onValueChange={(val) => handleSelectChange('propertyType', val)}
                    >
                      <SelectTrigger className="border-emerald-200">
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {PROPERTY_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-emerald-700 font-medium">Carpet Area (sq.ft)</Label>
                    <Input
                      name="carpetArea"
                      value={formData.carpetArea}
                      onChange={handleChange}
                      className="border-emerald-200"
                      placeholder="e.g., 900"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-emerald-700 font-medium">Budget Range</Label>
                    <Select
                      value={formData.budget}
                      onValueChange={(val) => handleSelectChange('budget', val)}
                    >
                      <SelectTrigger className="border-emerald-200">
                        <SelectValue placeholder="Select budget" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {BUDGET_RANGES.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Management */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-200">
              <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-4">
                <Users className="w-5 h-5" /> Management
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-purple-700 font-medium">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(val) => handleSelectChange('status', val)}
                  >
                    <SelectTrigger className="border-purple-200">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Lead">?? Lead</SelectItem>
                      <SelectItem value="FollowUp">?? Follow Up</SelectItem>
                      <SelectItem value="Connected">?? Connected</SelectItem>
                      <SelectItem value="Confirm">?? Confirm</SelectItem>
                      <SelectItem value="Converted">?? Converted (Work Started)</SelectItem>
                      <SelectItem value="Submitted">? Submitted (Work Done)</SelectItem>
                      <SelectItem value="Lost">?? Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!['Converted', 'Submitted', 'Lost'].includes(formData.status) && (
                  <div className="space-y-1.5">
                    <Label className="text-purple-700 font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Next Follow-up Date
                    </Label>
                    <Input
                      type="date"
                      name="followupDate"
                      value={formData.followupDate}
                      onChange={handleChange}
                      className="border-purple-200"
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-purple-700 font-medium flex items-center gap-2">
                    <Users className="w-4 h-4" /> Assign Sales
                  </Label>
                  <Select
                    value={formData.assignedSales}
                    onValueChange={(val) => handleSelectChange('assignedSales', val)}
                  >
                    <SelectTrigger className="border-purple-200">
                      <SelectValue placeholder="Select sales" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {salesUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-purple-700 font-medium flex items-center gap-2">
                    <Users className="w-4 h-4" /> Assign Designer
                  </Label>
                  <Select
                    value={formData.assignedDesigner}
                    onValueChange={(val) => handleSelectChange('assignedDesigner', val)}
                  >
                    <SelectTrigger className="border-purple-200">
                      <SelectValue placeholder="Select designer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {designerUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label className="text-indigo-700 font-medium flex items-center gap-2">
                <FileText className="w-4 h-4" /> Notes / Remarks
              </Label>
              <Textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="border-indigo-200"
                placeholder="Any additional notes..."
              />
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-indigo-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/office/clients')}
                className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg gap-2"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : isEditing ? 'Update Client' : 'Create Client'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientForm;